"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import "@/styles/home/logomarquee.css";

import { partnerLogos } from "@/constants/Data";

const SECONDS_PER_SEQUENCE = 26;

function PartnerLogo({ logo, hidden }) {
  const shared = {
    "aria-hidden": hidden || undefined,
    sizes: "120px",
    style: { width: `calc(var(--logo-w) * ${logo.scale ?? 1})` },
  };
  const alt = hidden ? "" : logo.name;

  if (!logo.lightIcon) {
    return (
      <Image
        {...shared}
        src={logo.icon}
        alt={alt}
        width={logo.width}
        height={logo.height}
        className="max-w-none h-auto object-contain"
      />
    );
  }

  return (
    <>
      <Image
        {...shared}
        src={logo.lightIcon}
        alt={alt}
        width={logo.lightWidth}
        height={logo.lightHeight}
        className="max-w-none h-auto object-contain dark:hidden"
      />
      <Image
        {...shared}
        src={logo.icon}
        alt=""
        aria-hidden
        width={logo.width}
        height={logo.height}
        className="max-w-none h-auto object-contain hidden dark:block"
      />
    </>
  );
}

export default function LogoMarquee() {
  const containerRef = useRef(null);

  const [copies, setCopies] = useState(2);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const track = container.querySelector(".logo-marquee__track");
      if (!track) return;

      const sequenceWidth = track.scrollWidth / copies;
      if (!sequenceWidth) return;

      const needed = Math.max(
        2,
        2 * Math.ceil(container.clientWidth / sequenceWidth)
      );
      setCopies((current) => (current === needed ? current : needed));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [copies]);

  const track = Array.from({ length: copies }, () => partnerLogos).flat();

  return (
    <div
      ref={containerRef}
      className="logo-marquee relative w-full overflow-hidden [--logo-w:72px] md:[--logo-w:96px] xl:[--logo-w:120px] bg-white dark:bg-[#0A050E]"
      style={{
        "--marquee-duration": `${(copies / 2) * SECONDS_PER_SEQUENCE}s`,
      }}
      aria-label="Technologies and platforms we work with"
    >
      <p
        className="font-manrope font-semibold text-center px-5 md:px-10 xl:px-[100px] pt-[20px] xl:pt-[30px] text-[12px] mobile:text-[13px] md:text-[15px] xl:text-[18px] leading-normal"
      >
        Built Across Leading Enterprise Technology Ecosystems
      </p>

      <div className="logo-marquee__track flex items-center">
        {track.map((logo, index) => (
          <div
            key={`${logo.name}-${index}`}
            className="shrink-0 flex items-center justify-center pr-[48px] py-[calc(var(--logo-w)*0.2)]"
          >
            <PartnerLogo logo={logo} hidden={index >= partnerLogos.length} />
          </div>
        ))}
      </div>
    </div>
  );
}
